/**
 * The profile, credits and privacy screens (spec 9.6, build spec 4.4). Plain dark screens, no
 * prototype. The profile is reached by tapping the day-goal ring (build spec 8).
 *
 * The privacy screen states the whole personal-data inventory of spec 4.2 — username, PIN and
 * results, nothing else. The founder decided on 2026-09-22 that the pilot needs no policy URL
 * (build spec §1); the store listing's policy comes with the store effort (ADR 0005).
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { authedJson, API_URL } from "../shared/api.js";
import type { Me, TopicCredit } from "../shared/api-types.js";
import { endSession } from "../shared/auth-store.js";
import { germanMessage } from "../shared/errors.js";
import { storage } from "../shared/storage.js";
import { avatarFor } from "../shared/avatars.js";
import { ExternalLink } from "../shared/platform.js";
import { Button } from "../shared/primitives/Button.js";
import { ChevronLeftIcon, ChevronRightIcon } from "../shared/primitives/Icons.js";
import { Splash } from "./states.js";

function BackBar({ title }: { title: string }) {
  const navigate = useNavigate();
  return (
    <div
      className="flex items-center gap-2 px-5 pt-5 pb-4"
      style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top, 0px))" }}
    >
      <Button
        onClick={() => void navigate(-1)}
        aria-label="Zurück"
        className="flex items-center text-white opacity-70"
      >
        <ChevronLeftIcon size={22} />
      </Button>
      <h1 className="m-0 text-[20px] font-extrabold text-white">{title}</h1>
    </div>
  );
}

function Row({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-2xl bg-[var(--color-tile)] px-5 py-4 text-left text-[16px] font-semibold text-white"
    >
      {label}
      <span className="opacity-50">
        <ChevronRightIcon />
      </span>
    </Button>
  );
}

export function ProfileScreen() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void authedJson<Me>("/me")
      .then(setMe)
      .catch((err: unknown) => setError(germanMessage(err)));
  }, []);

  async function logout() {
    // Spec 8.3: a visible "Abmelden" is how shared devices are handled, not a short expiry.
    const refreshToken = await storage.get("refreshToken");
    if (refreshToken) {
      await fetch(API_URL + "/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {
        // Even if the server never hears about it, the local session is gone below.
      });
    }
    await endSession();
    void navigate("/login", { replace: true });
  }

  if (!me && !error) return <Splash />;

  return (
    <div className="flex h-full flex-col">
      <BackBar title="Dein Profil" />
      <div className="flex-1 overflow-y-auto px-5 pb-8">
        {error && <p className="text-[15px] text-[var(--color-bad)]">{error}</p>}
        {me && (
          <>
            <div className="mb-7 flex items-center gap-4">
              <span className="text-[56px] leading-none" aria-hidden="true">
                {avatarFor(me.number)}
              </span>
              <div>
                <div className="text-[22px] font-extrabold text-white">{me.username}</div>
                <div className="text-[15px] text-white opacity-65">Klasse {me.classNumber}</div>
              </div>
            </div>

            <div className="mb-7 rounded-2xl bg-[var(--color-tile)] px-5 py-4">
              <div className="text-[15px] text-white opacity-65">Heute geschafft</div>
              <div className="text-[28px] font-black text-[var(--color-accent)]">
                {me.today} von {me.dayGoal}
              </div>
            </div>
          </>
        )}

        <div className="flex flex-col gap-2.5">
          <Row label="Quellen und Lizenzen" onClick={() => void navigate("/quellen")} />
          <Row label="Datenschutz" onClick={() => void navigate("/datenschutz")} />
        </div>

        <Button
          onClick={() => void logout()}
          className="mt-8 w-full rounded-full border-2 border-white/25 py-4 text-[17px] font-extrabold text-white"
        >
          Abmelden
        </Button>
      </div>
    </div>
  );
}

export function CreditsScreen() {
  const [topics, setTopics] = useState<TopicCredit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void authedJson<TopicCredit[]>("/topics/credits")
      .then(setTopics)
      .catch((err: unknown) => setError(germanMessage(err)));
  }, []);

  if (!topics && !error) return <Splash />;

  return (
    <div className="flex h-full flex-col">
      <BackBar title="Quellen und Lizenzen" />
      <div className="flex-1 overflow-y-auto px-5 pb-8">
        <p className="mt-0 mb-6 text-[15px] leading-[1.5] text-white opacity-70">
          Die Texte auf den Karten sind nach Artikeln aus dem Klexikon geschrieben, für Kinder
          umgeschrieben und gekürzt (mit KI). Karten und Fragen stehen ebenfalls unter CC BY-SA 4.0.
        </p>
        {error && <p className="text-[15px] text-[var(--color-bad)]">{error}</p>}
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {topics?.map((topic) => (
            <li key={topic.title} className="rounded-2xl bg-[var(--color-tile)] px-5 py-4">
              <div className="text-[16px] font-bold text-white">{topic.title}</div>
              <div className="mt-1 text-[13px] text-white opacity-65">
                Nach «{topic.sourceTitle}» aus dem Klexikon ·{" "}
                <ExternalLink href={topic.sourceUrl} className="text-[var(--color-accent)]">
                  Artikel
                </ExternalLink>{" "}
                ·{" "}
                <ExternalLink href={topic.licenceUrl} className="text-[var(--color-accent)]">
                  {topic.licence}
                </ExternalLink>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function PrivacyScreen() {
  return (
    <div className="flex h-full flex-col">
      <BackBar title="Datenschutz" />
      <div className="flex-1 overflow-y-auto px-5 pb-8 text-[15px] leading-[1.6] text-white opacity-80">
        <p className="mt-0">
          Scrolivo speichert von dir nur deinen Benutzernamen (zum Beispiel Klasse10_S01), deinen
          PIN und deine Ergebnisse aus den Fragen.
        </p>
        <p>
          Scrolivo speichert <b>keinen</b> Namen, kein Geburtsdatum, keine Adresse, keine
          E-Mail-Adresse und nichts über dein Gerät.
        </p>
        <p>
          Deine Lehrerin oder dein Lehrer sieht, welche Themen du abgeschlossen hast und wie viele
          Fragen du richtig beantwortet hast.
        </p>
        <p>
          Wenn du dein Konto löschen möchtest, sag es deiner Lehrerin oder deinem Lehrer. Sie können
          es löschen, und dann sind alle deine Ergebnisse weg.
        </p>
        <p className="mb-0">In Scrolivo gibt es keine Werbung und keine fremden Dienste.</p>
      </div>
    </div>
  );
}
